class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = Object.create(null);
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.loadingMarkup = this.predictiveSearchResults?.innerHTML || '';
    this.searchTerm = '';
    this.renderedQuery = '';
    this.dismissedQuery = null;
    this.requestId = 0;
    this.isOpen = false;
    this.overlay = document.querySelector('.header__overlay');
    this.headerTop = document.querySelector('.header__top');
    this.headerBottom = document.querySelector('.header__bottom');
    this.promoModal = this.querySelector('.search__modal');
    PredictiveSearch.instanceCount = (PredictiveSearch.instanceCount || 0) + 1;
    this.resultsId = `predictive-results-${PredictiveSearch.instanceCount}`;
    if (this.input && this.predictiveSearchResults) this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-controls', this.resultsId);
    this.input.removeAttribute('aria-owns');
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    if (this.closest('.faf-studio-header') && this.input.classList.contains('snize-exclude-input')) {
      const field = this.input.closest('.field');
      if (field) {
        const keepNativeSearchField = () => {
          if (field.classList.contains('snize-custom-widget-opener')) field.classList.remove('snize-custom-widget-opener');
        };
        keepNativeSearchField();
        this.searchWidgetObserver = new MutationObserver(keepNativeSearchField);
        this.searchWidgetObserver.observe(field, { attributes: true, attributeFilter: ['class'] });
        const keepNativeFieldEvent = (event) => {
          if (event.target instanceof Element && event.target.closest('.field') === field) event.stopPropagation();
        };
        ['pointerdown', 'mousedown', 'touchstart', 'touchend', 'click', 'focusin'].forEach((type) => {
          this.input.form.addEventListener(type, keepNativeFieldEvent, { capture: true, passive: true });
        });
        this.input.form.addEventListener('focus', (event) => {
          if (event.target !== this.input) return;
          event.stopPropagation();
          this.onFocus();
        }, true);
      }
    }
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('input', this.onInput.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('compositionend', () => this.onChange());
  }

  disconnectedCallback() {
    this.cancelSearch();
    this.searchWidgetObserver?.disconnect();
    clearTimeout(this.liveRegionTimer);
  }

  getQuery() { return this.input.value.trim(); }

  cancelSearch() {
    this.requestId += 1;
    this.abortController?.abort();
    this.abortController = null;
  }

  onInput() {
    this.dismissedQuery = null;
    this.closeResults();
    this.searchTerm = this.getQuery();
    this.renderedQuery = '';
    this.removeAttribute('results');
    this.predictiveSearchResults.innerHTML = this.loadingMarkup;
    if (this.contains(document.activeElement)) this.setHeaderState(true);
    this.showAssist(!this.searchTerm && this.contains(document.activeElement));
  }

  onChange() {
    this.searchTerm = this.getQuery();
    if (!this.contains(document.activeElement) || this.dismissedQuery === this.searchTerm) return;
    if (!this.searchTerm) {
      this.closeResults();
      this.showAssist(true);
      return;
    }
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) { if (!this.getQuery()) event.preventDefault(); }

  onFormReset(event) {
    event.preventDefault();
    this.closeResults(true);
    this.dismissedQuery = null;
    this.input.focus();
    this.setHeaderState(true);
    this.showAssist(true);
  }

  onFocus() {
    this.dismissedQuery = null;
    this.setHeaderState(true);
    const query = this.getQuery();
    this.showAssist(!query);
    if (!query) return;
    if (query === this.renderedQuery && this.getAttribute('results') === 'true') this.open();
    else this.onChange();
  }

  onFocusOut(event) {
    if (event.relatedTarget && this.contains(event.relatedTarget)) return;
    const leave = () => { if (!this.contains(document.activeElement)) this.close(); };
    if (event.relatedTarget) this.close();
    else setTimeout(leave, 0);
  }

  onBlur() { this.input.blur(); this.close(); }

  onKeyup(event) {
    if ((event.key === 'Escape' || event.code === 'Escape') && this.escapeHandled) {
      event.stopPropagation();
      this.escapeHandled = false;
    }
  }

  onKeydown(event) {
    if (event.isComposing || event.keyCode === 229) return;
    const key = event.key || event.code;
    if (key === 'Escape') {
      if (!this.isOpen && !this.hasAttribute('loading') && !this.getQuery() && this.promoModal?.classList.contains('search__modal--hidden') !== false) return;
      event.preventDefault();
      event.stopPropagation();
      this.escapeHandled = true;
      this.dismissedQuery = this.getQuery();
      this.close();
      if (document.activeElement !== this.input && this.contains(document.activeElement)) {
        this.input.focus();
        this.dismissedQuery = this.getQuery();
        this.close();
      }
      return;
    }
    if (event.target !== this.input) return;
    if ((key === 'ArrowDown' || key === 'ArrowUp') && this.getQuery() === this.renderedQuery && this.getAttribute('results') === 'true') {
      event.preventDefault();
      this.open();
      this.switchOption(key === 'ArrowDown' ? 'down' : 'up');
    } else if (key === 'Enter' && this.isOpen && this.getQuery() === this.renderedQuery) {
      if (this.querySelector('[role="option"][aria-selected="true"]')) {
        event.preventDefault();
        this.selectOption();
      }
    }
  }

  switchOption(direction) {
    const options = Array.from(this.predictiveSearchResults.querySelectorAll('[role="option"]'));
    if (!options.length) return;
    const current = options.findIndex(option => option.getAttribute('aria-selected') === 'true');
    const next = current < 0 ? (direction === 'down' ? 0 : options.length - 1) : (current + (direction === 'down' ? 1 : -1) + options.length) % options.length;
    options.forEach((option, index) => option.setAttribute('aria-selected', String(index === next)));
    this.input.setAttribute('aria-activedescendant', options[next].id);
    options[next].scrollIntoView?.({ block: 'nearest' });
  }

  selectOption() {
    const selected = this.querySelector('[role="option"][aria-selected="true"]');
    if (!selected) return false;
    const action = selected.matches('a, button') ? selected : selected.querySelector('a[href], button');
    if (!action) return false;
    action.click();
    return true;
  }

  updateSearchForTerm() {
    this.querySelectorAll('[role="option"][aria-selected="true"]').forEach(option => option.setAttribute('aria-selected', 'false'));
    this.input.removeAttribute('aria-activedescendant');
  }

  isCurrentRequest(requestId, searchTerm) {
    return this.isConnected && this.requestId === requestId && this.getQuery() === searchTerm && this.dismissedQuery !== searchTerm && this.contains(document.activeElement);
  }

  async getSearchResults(searchTerm) {
    this.cancelSearch();
    const requestId = this.requestId;
    this.abortController = new AbortController();
    const signal = this.abortController.signal;
    this.showAssist(false);
    this.setLiveRegionLoadingState();
    try {
      let markup = this.cachedResults[searchTerm];
      if (!markup) {
        const response = await fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[type]=product,collection,page,article&resources[limit]=8&resources[limit_scope]=all&section_id=predictive-search`, { signal });
        if (!response.ok) throw new Error('Predictive search unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const section = html.querySelector('#shopify-section-predictive-search');
        if (!section || !section.querySelector('[role="listbox"]')) throw new Error('Invalid predictive search response');
        markup = section.innerHTML;
        if (Object.keys(this.cachedResults).length >= 20) delete this.cachedResults[Object.keys(this.cachedResults)[0]];
        this.cachedResults[searchTerm] = markup;
      }
      if (this.isCurrentRequest(requestId, searchTerm)) this.renderSearchResults(markup, searchTerm);
    } catch (error) {
      if (this.isCurrentRequest(requestId, searchTerm)) this.closeResults();
    }
  }

  setLiveRegionLoadingState() {
    this.setLiveRegionText(this.getAttribute('data-loading-text') || '');
    this.setAttribute('loading', 'true');
  }

  setLiveRegionText(text) {
    const status = this.querySelector('.predictive-search-status');
    if (!status) return;
    clearTimeout(this.liveRegionTimer);
    status.textContent = text;
    status.setAttribute('aria-hidden', 'false');
    this.liveRegionTimer = setTimeout(() => status.setAttribute('aria-hidden', 'true'), 1000);
  }

  renderSearchResults(markup, query = this.getQuery()) {
    this.predictiveSearchResults.innerHTML = markup;
    const ids = new Map();
    this.predictiveSearchResults.querySelectorAll('[id]').forEach((element, index) => {
      const unique = `${this.resultsId}-${index}`;
      ids.set(element.id, unique);
      element.id = unique;
    });
    this.predictiveSearchResults.querySelectorAll('[aria-labelledby]').forEach(element => {
      element.setAttribute('aria-labelledby', element.getAttribute('aria-labelledby').split(/\s+/).map(id => ids.get(id) || id).join(' '));
    });
    const listbox = this.predictiveSearchResults.querySelector('[role="listbox"]');
    if (listbox) listbox.id = this.resultsId;
    this.renderedQuery = query;
    this.setAttribute('results', 'true');
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent || '');
  }

  showAssist(visible) { this.promoModal?.classList.toggle('search__modal--hidden', !visible); }

  setHeaderState(active) {
    if (!this.classList.contains('search-modal__form')) return;
    const otherSearch = document.activeElement?.closest?.('predictive-search');
    if (!active && otherSearch && otherSearch !== this && otherSearch.classList.contains('search-modal__form')) return;
    this.overlay?.classList.toggle('visible', active);
    this.headerTop?.classList.toggle('header__top--search', active);
    this.headerBottom?.classList.toggle('header__bottom--visible', active);
  }

  open() {
    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
    this.showAssist(false);
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.setHeaderState(false);
    this.showAssist(false);
  }

  closeResults(clearSearchTerm = false) {
    this.cancelSearch();
    this.updateSearchForTerm();
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.isOpen = false;
    this.predictiveSearchResults.removeAttribute('style');
    if (clearSearchTerm) {
      this.input.value = '';
      this.searchTerm = '';
      this.renderedQuery = '';
      this.removeAttribute('results');
      this.predictiveSearchResults.innerHTML = this.loadingMarkup;
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
